import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Check } from 'lucide-react';
import { Button, Modal, Input, Select } from '@/components/ui';
import { patientService } from '@/services/patientService';
import { procedureTypeService } from '@/services/procedureTypeService';
import { resourceService } from '@/services/resourceService';
import { appointmentService } from '@/services/appointmentService';
import type { PatientSearchResult, CreatePatientRequest } from '@/types/patient';
import type { ProcedureType, Resource } from '@/types';
import type { AppointmentType, AvailableTimeSlot } from '@/types/appointment';
import styles from './AppointmentModal.module.css';

interface AppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** Pre-selected date (YYYY-MM-DD). */
  initialDate?: string;
  /** Pre-selected resource ID. */
  initialResourceId?: string;
}

type WizardStep = 1 | 2 | 3 | 4 | 5;

const STEP_LABELS: Record<WizardStep, string> = {
  1: 'Paciente',
  2: 'Procedimento',
  3: 'Recurso e Data',
  4: 'Horário',
  5: 'Confirmar',
};

/**
 * Returns today's date as YYYY-MM-DD string.
 */
function getTodayString(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Formats a CPF string (11 digits) as 000.000.000-00.
 */
function formatCpf(cpf: string): string {
  const digits = cpf.replace(/\D/g, '');
  if (digits.length !== 11) return cpf;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

/**
 * Multi-step appointment creation wizard.
 *
 * Step 1: Search and select patient (with inline creation)
 * Step 2: Select procedure type
 * Step 3: Select resource and date
 * Step 4: Select available time slot
 * Step 5: Review and confirm
 *
 * Includes a fit-in toggle that marks the appointment type as FIT_IN.
 *
 * Accessibility:
 * - Step navigation via keyboard
 * - aria-label on interactive elements
 * - role="listbox" for option lists
 * - Focus management on step changes
 */
export function AppointmentModal({
  isOpen,
  onClose,
  onSuccess,
  initialDate,
  initialResourceId,
}: AppointmentModalProps) {
  const queryClient = useQueryClient();
  const stepContentRef = useRef<HTMLDivElement>(null);

  // Wizard state
  const [currentStep, setCurrentStep] = useState<WizardStep>(1);
  const [isFitIn, setIsFitIn] = useState(false);
  const [notes, setNotes] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  // Step 1: Patient
  const [patientQuery, setPatientQuery] = useState('');
  const [debouncedPatientQuery, setDebouncedPatientQuery] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<PatientSearchResult | null>(null);
  const [showInlineCreate, setShowInlineCreate] = useState(false);
  const patientDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Inline patient creation
  const [inlineName, setInlineName] = useState('');
  const [inlineCpf, setInlineCpf] = useState('');
  const [inlinePhone, setInlinePhone] = useState('');

  // Step 2: Procedure type
  const [selectedProcedure, setSelectedProcedure] = useState<ProcedureType | null>(null);

  // Step 3: Resource and date
  const [selectedResourceId, setSelectedResourceId] = useState(initialResourceId ?? '');
  const [selectedDate, setSelectedDate] = useState(initialDate ?? getTodayString());

  // Step 4: Time slot
  const [selectedSlot, setSelectedSlot] = useState<AvailableTimeSlot | null>(null);

  /**
   * Resets all wizard state and closes the modal.
   * Called instead of onClose directly so state is clean for next open.
   */
  const handleClose = useCallback(() => {
    onClose();
    setCurrentStep(1);
    setIsFitIn(false);
    setNotes('');
    setFormError(null);
    setPatientQuery('');
    setDebouncedPatientQuery('');
    setSelectedPatient(null);
    setShowInlineCreate(false);
    setInlineName('');
    setInlineCpf('');
    setInlinePhone('');
    setSelectedProcedure(null);
    setSelectedResourceId(initialResourceId ?? '');
    setSelectedDate(initialDate ?? getTodayString());
    setSelectedSlot(null);
  }, [onClose, initialDate, initialResourceId]);

  // Debounce patient search
  useEffect(() => {
    if (patientDebounceRef.current) {
      clearTimeout(patientDebounceRef.current);
    }
    patientDebounceRef.current = setTimeout(() => {
      setDebouncedPatientQuery(patientQuery);
    }, 300);
    return () => {
      if (patientDebounceRef.current) {
        clearTimeout(patientDebounceRef.current);
      }
    };
  }, [patientQuery]);

  // Focus step content on step change
  useEffect(() => {
    if (stepContentRef.current) {
      const firstFocusable = stepContentRef.current.querySelector<HTMLElement>(
        'button, input, select, [tabindex]:not([tabindex="-1"])',
      );
      firstFocusable?.focus();
    }
  }, [currentStep]);

  // Data queries
  const { data: patientResults } = useQuery({
    queryKey: ['patient-search', debouncedPatientQuery],
    queryFn: () => patientService.searchPatients(debouncedPatientQuery, 1, 8),
    enabled: debouncedPatientQuery.length >= 2 && currentStep === 1,
  });

  const { data: procedureTypes = [] } = useQuery({
    queryKey: ['procedure-types-active'],
    queryFn: () => procedureTypeService.getProcedureTypes(),
    enabled: currentStep >= 2,
  });

  const activeProcedures = useMemo(
    () => procedureTypes.filter((p) => p.isActive),
    [procedureTypes],
  );

  const { data: activeResources = [] } = useQuery({
    queryKey: ['resources-active'],
    queryFn: () => resourceService.getResources({ isActive: true }),
    enabled: currentStep >= 3,
  });

  const resourceOptions = useMemo(
    () => activeResources.map((r: Resource) => ({ value: r.id, label: r.name })),
    [activeResources],
  );

  const {
    data: availableSlots = [],
    isLoading: isSlotsLoading,
  } = useQuery({
    queryKey: ['available-slots', selectedResourceId, selectedDate, selectedProcedure?.id],
    queryFn: () =>
      appointmentService.getAvailableSlots(selectedResourceId, selectedDate, selectedProcedure!.id),
    enabled:
      currentStep >= 4 &&
      selectedResourceId !== '' &&
      selectedDate !== '' &&
      selectedProcedure !== null,
  });

  // Create appointment mutation
  const createMutation = useMutation({
    mutationFn: () => {
      if (!selectedPatient || !selectedProcedure || !selectedSlot) {
        throw new Error('Missing required fields');
      }
      const appointmentType: AppointmentType = isFitIn ? 'FIT_IN' : 'REGULAR';
      return appointmentService.createAppointment({
        patientId: selectedPatient.id,
        resourceId: selectedResourceId,
        procedureTypeId: selectedProcedure.id,
        date: selectedDate,
        startTime: selectedSlot.startTime,
        appointmentType,
        notes: notes.trim() || null,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daily-schedule'] });
      queryClient.invalidateQueries({ queryKey: ['clinic-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['available-slots'] });
      onSuccess();
      handleClose();
    },
    onError: () => {
      setFormError('Erro ao criar agendamento. Tente novamente.');
    },
  });

  // Inline patient creation mutation
  const inlineCreateMutation = useMutation({
    mutationFn: (payload: CreatePatientRequest) => patientService.createPatient(payload),
    onSuccess: (patient) => {
      setSelectedPatient({
        id: patient.id,
        name: patient.name,
        cpf: patient.cpf,
        phone: patient.phone,
        isActive: patient.isActive,
      });
      setShowInlineCreate(false);
      queryClient.invalidateQueries({ queryKey: ['patient-search'] });
    },
    onError: () => {
      setFormError('Erro ao cadastrar paciente. Tente novamente.');
    },
  });

  const handleInlineCreate = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      setFormError(null);
      if (!inlineName.trim()) {
        setFormError('Nome é obrigatório.');
        return;
      }
      if (!inlineCpf.trim()) {
        setFormError('CPF é obrigatório.');
        return;
      }
      if (!inlinePhone.trim()) {
        setFormError('Telefone é obrigatório.');
        return;
      }
      inlineCreateMutation.mutate({
        name: inlineName.trim(),
        email: null,
        cpf: inlineCpf.replace(/\D/g, ''),
        phone: inlinePhone.replace(/\D/g, ''),
        birthDate: null,
        healthPlanName: null,
        healthPlanNumber: null,
        notes: null,
      });
    },
    [inlineName, inlineCpf, inlinePhone, inlineCreateMutation],
  );

  const canProceedToStep = useCallback(
    (step: WizardStep): boolean => {
      switch (step) {
        case 2:
          return selectedPatient !== null;
        case 3:
          return selectedProcedure !== null;
        case 4:
          return selectedResourceId !== '' && selectedDate !== '';
        case 5:
          return selectedSlot !== null;
        default:
          return true;
      }
    },
    [selectedPatient, selectedProcedure, selectedResourceId, selectedDate, selectedSlot],
  );

  const goToNextStep = useCallback(() => {
    setFormError(null);
    if (currentStep < 5 && canProceedToStep((currentStep + 1) as WizardStep)) {
      setCurrentStep((prev) => (prev + 1) as WizardStep);
    }
  }, [currentStep, canProceedToStep]);

  const goToPreviousStep = useCallback(() => {
    setFormError(null);
    if (currentStep > 1) {
      setCurrentStep((prev) => (prev - 1) as WizardStep);
    }
  }, [currentStep]);

  const handleConfirm = useCallback(() => {
    setFormError(null);
    createMutation.mutate();
  }, [createMutation]);

  // Step indicators
  const stepIndicators = (
    <div className={styles.steps} role="navigation" aria-label="Etapas do agendamento">
      {([1, 2, 3, 4, 5] as WizardStep[]).map((step, index) => {
        let stepClass = styles.step;
        if (step === currentStep) stepClass += ` ${styles.active}`;
        else if (step < currentStep) stepClass += ` ${styles.completed}`;

        return (
          <div key={step}>
            {index > 0 && <span className={styles.stepConnector} aria-hidden="true" />}
            <span className={stepClass} aria-current={step === currentStep ? 'step' : undefined}>
              <span className={styles.stepNumber} aria-hidden="true">
                {step < currentStep ? <Check size={12} /> : step}
              </span>
              <span className="sr-only">
                {step < currentStep ? `Etapa ${step} concluida: ` : `Etapa ${step}: `}
              </span>
              <span>{STEP_LABELS[step]}</span>
            </span>
          </div>
        );
      })}
    </div>
  );

  // Step 1: Patient selection
  const renderStep1 = () => (
    <div className={styles.stepContent}>
      <p className={styles.stepTitle}>Selecione o paciente</p>

      {selectedPatient ? (
        <div className={styles.selectedPatient}>
          <div className={styles.selectedPatientInfo}>
            <span className={styles.selectedPatientName}>{selectedPatient.name}</span>
            <span className={styles.selectedPatientCpf}>{formatCpf(selectedPatient.cpf)}</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setSelectedPatient(null)}>
            Trocar
          </Button>
        </div>
      ) : (
        <div className={styles.patientSearch}>
          <Input
            label="Buscar paciente"
            value={patientQuery}
            onChange={(e) => setPatientQuery(e.target.value)}
            placeholder="Nome, CPF ou telefone"
            leftIcon={<Search size={16} />}
            fullWidth
          />

          {patientResults && patientResults.items.length > 0 && (
            <div className={styles.patientResults} role="listbox" aria-label="Resultados da busca">
              {patientResults.items.map((patient) => (
                <button
                  key={patient.id}
                  type="button"
                  className={styles.patientResultItem}
                  onClick={() => setSelectedPatient(patient)}
                  role="option"
                  aria-selected={false}
                >
                  <span className={styles.patientResultName}>{patient.name}</span>
                  <span className={styles.patientResultCpf}>{formatCpf(patient.cpf)}</span>
                </button>
              ))}
            </div>
          )}

          {patientResults && patientResults.items.length === 0 && debouncedPatientQuery.length >= 2 && (
            <p>Nenhum paciente encontrado.</p>
          )}

          {!showInlineCreate ? (
            <button
              type="button"
              className={styles.inlineCreateLink}
              onClick={() => setShowInlineCreate(true)}
            >
              Cadastrar novo paciente
            </button>
          ) : (
            <form
              className={styles.inlineForm}
              onSubmit={handleInlineCreate}
              noValidate
            >
              <p className={styles.stepTitle}>Cadastro rapido</p>
              <div className={styles.inlineFormRow}>
                <Input
                  label="Nome"
                  value={inlineName}
                  onChange={(e) => setInlineName(e.target.value)}
                  placeholder="Nome completo"
                  required
                  fullWidth
                />
              </div>
              <div className={styles.inlineFormRow}>
                <Input
                  label="CPF"
                  value={inlineCpf}
                  onChange={(e) => setInlineCpf(e.target.value)}
                  placeholder="000.000.000-00"
                  required
                  fullWidth
                />
                <Input
                  label="Telefone"
                  value={inlinePhone}
                  onChange={(e) => setInlinePhone(e.target.value)}
                  placeholder="(00) 00000-0000"
                  required
                  fullWidth
                />
              </div>
              <div className={styles.inlineFormActions}>
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={() => setShowInlineCreate(false)}
                >
                  Cancelar
                </Button>
                <Button size="sm" type="submit" isLoading={inlineCreateMutation.isPending}>
                  Cadastrar
                </Button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );

  // Step 2: Procedure type selection
  const renderStep2 = () => (
    <div className={styles.stepContent}>
      <p className={styles.stepTitle}>Selecione o procedimento</p>
      <div className={styles.optionsList} role="listbox" aria-label="Tipos de procedimento">
        {activeProcedures.map((procedure) => {
          const isSelected = selectedProcedure?.id === procedure.id;
          return (
            <button
              key={procedure.id}
              type="button"
              className={[styles.optionItem, isSelected ? styles.selected : '']
                .filter(Boolean)
                .join(' ')}
              onClick={() => setSelectedProcedure(procedure)}
              role="option"
              aria-selected={isSelected}
            >
              <span className={styles.optionName}>{procedure.name}</span>
              <span className={styles.optionDetail}>{procedure.durationMinutes} min</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  // Step 3: Resource and date selection
  const renderStep3 = () => (
    <div className={styles.stepContent}>
      <p className={styles.stepTitle}>Selecione recurso e data</p>
      <div className={styles.resourceDateRow}>
        <Select
          label="Recurso"
          options={resourceOptions}
          value={selectedResourceId}
          onChange={(e) => {
            setSelectedResourceId(e.target.value);
            setSelectedSlot(null);
          }}
          placeholder="Selecione um recurso"
          fullWidth
        />
        <Input
          label="Data"
          type="date"
          value={selectedDate}
          onChange={(e) => {
            setSelectedDate(e.target.value);
            setSelectedSlot(null);
          }}
          fullWidth
        />
      </div>
      <div className={styles.fitInToggle}>
        <label className={styles.fitInLabel}>
          <input
            type="checkbox"
            checked={isFitIn}
            onChange={(e) => setIsFitIn(e.target.checked)}
            className={styles.fitInCheckbox}
          />
          <span>Encaixe (Fit-in)</span>
        </label>
      </div>
    </div>
  );

  // Step 4: Time slot selection
  const renderStep4 = () => (
    <div className={styles.stepContent}>
      <p className={styles.stepTitle}>Selecione o horário</p>
      {isSlotsLoading ? (
        <div className={styles.loadingSlots} role="status" aria-busy="true">
          Carregando horários disponíveis...
        </div>
      ) : availableSlots.length === 0 ? (
        <div className={styles.emptySlots}>
          Nenhum horário disponível para esta data e recurso.
        </div>
      ) : (
        <div className={styles.optionsList} role="listbox" aria-label="Horários disponíveis">
          {availableSlots.map((slot) => {
            const isSelected =
              selectedSlot?.startTime === slot.startTime &&
              selectedSlot?.endTime === slot.endTime;
            return (
              <button
                key={`${slot.startTime}-${slot.endTime}`}
                type="button"
                className={[styles.optionItem, isSelected ? styles.selected : '']
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => setSelectedSlot(slot)}
                role="option"
                aria-selected={isSelected}
              >
                <span className={styles.optionName}>
                  {slot.startTime} - {slot.endTime}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  // Step 5: Confirmation
  const renderStep5 = () => {
    const resourceName = activeResources.find((r: Resource) => r.id === selectedResourceId)?.name ?? '';
    return (
      <div className={styles.stepContent}>
        <p className={styles.stepTitle}>Confirme o agendamento</p>
        <div className={styles.summary}>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Paciente</span>
            <span className={styles.summaryValue}>{selectedPatient?.name}</span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Procedimento</span>
            <span className={styles.summaryValue}>{selectedProcedure?.name}</span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Recurso</span>
            <span className={styles.summaryValue}>{resourceName}</span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Data</span>
            <span className={styles.summaryValue}>
              {new Date(selectedDate + 'T12:00:00').toLocaleDateString('pt-BR')}
            </span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Horário</span>
            <span className={styles.summaryValue}>
              {selectedSlot?.startTime} - {selectedSlot?.endTime}
            </span>
          </div>
          {isFitIn && (
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Tipo</span>
              <span className={styles.summaryValue}>Encaixe</span>
            </div>
          )}
        </div>

        <div className={styles.textareaWrapper}>
          <label htmlFor="appointment-notes" className={styles.textareaLabel}>
            Observações (opcional)
          </label>
          <textarea
            id="appointment-notes"
            className={styles.textarea}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder="Observações sobre o agendamento..."
          />
        </div>
      </div>
    );
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      case 5:
        return renderStep5();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Novo Agendamento" size="lg">
      <div className={styles.wizard}>
        {stepIndicators}

        <div ref={stepContentRef}>{renderCurrentStep()}</div>

        {formError && (
          <p className={styles.formError} role="alert">
            {formError}
          </p>
        )}

        <div className={styles.wizardNav}>
          <div>
            {currentStep > 1 && (
              <Button variant="ghost" onClick={goToPreviousStep}>
                Voltar
              </Button>
            )}
          </div>
          <div className={styles.wizardNavRight}>
            <Button variant="ghost" onClick={handleClose}>
              Cancelar
            </Button>
            {currentStep < 5 ? (
              <Button
                onClick={goToNextStep}
                disabled={!canProceedToStep((currentStep + 1) as WizardStep)}
              >
                Próximo
              </Button>
            ) : (
              <Button
                onClick={handleConfirm}
                isLoading={createMutation.isPending}
                disabled={createMutation.isPending}
              >
                Confirmar Agendamento
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
